import type { CSSProperties } from 'react';
import { useEffect, useId, useRef, useState } from 'react';

export interface DefaultGlowRingProps {
  /** Battery state of charge, 0-100. Drives both the ring fill and the count-up. */
  percent: number;
  /** Small caps label under the percentage (e.g. "BATTERY" / "CHARGING"). */
  label?: string;
  /** Enables the halo pulse + wave animations. */
  active?: boolean;
  /** Outer square px. Halo extends beyond the SVG viewBox. */
  size?: number;
  /** Which semantic colour drives the ring + halo. */
  tone?: 'accent' | 'charging';
}

const R = 88;
const C = 2 * Math.PI * R;
const CENTRE_X = 110;
const CENTRE_Y = 110;
const INNER_R = R - 14;          // centre disc radius
const TOP_Y = CENTRE_Y - INNER_R;  // highest fill Y
const BOTTOM_Y = CENTRE_Y + INNER_R; // lowest fill Y

/**
 * Premium charging visual v2 — fills from the inside like a battery
 * rather than tracing the circumference alone. Composition:
 *
 *   1. outer blurred halo  (pulses when `active`)
 *   2. inner tighter halo  (static)
 *   3. centre disc         (var(--evi-surface) punch-through)
 *   4. energy liquid       (rises from bottom to a height proportional to SOC,
 *                           two rippling wave paths on the surface)
 *   5. track ring          (muted full circumference)
 *   6. progress arc        (charging-coloured, fills on mount)
 *   7. rim pulse           (the arc breathes via opacity, no more sweeping shine)
 *   8. dual readout        (neutral text underneath, white text clipped to
 *                           the fill region so the digits turn white as the
 *                           liquid rises past them)
 *
 * Hot-path animations run on `transform` / `opacity` / SVG attribute values
 * only. `filter: blur()` is applied once to the static halos and never
 * animated. `prefers-reduced-motion: reduce` clamps everything.
 */
export function DefaultGlowRing({
  percent,
  label,
  active = false,
  size = 300,
  tone = 'charging',
}: DefaultGlowRingProps) {
  const id = useId().replace(/:/g, '');
  const clamped = Math.max(0, Math.min(100, percent));
  const end = (C * clamped) / 100;
  const targetFillY = BOTTOM_Y - (BOTTOM_Y - TOP_Y) * (clamped / 100);

  const ringColor = tone === 'charging' ? 'var(--evi-charging)' : 'var(--evi-accent)';
  // `glowColor` (the pre-alpha'd rgba token) used to drive the halos, but
  // the new halo gradients build their own alpha ramps via color-mix off
  // `ringColor` so colour matches the ring exactly. Left aliased here in
  // case a downstream consumer wants the raw token.

  // ── Number count-up + fill-height — both rAF-driven so the fill rect Y
  // and the displayed number move in lockstep on mount. ─────────────────
  const [displayed, setDisplayed] = useState(0);
  const [fillY, setFillY] = useState(BOTTOM_Y);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const fromValueRef = useRef(0);
  const fromFillRef = useRef(BOTTOM_Y);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setDisplayed(clamped);
      setFillY(targetFillY);
      return () => {};
    }

    // Wind-up duration — 4th iteration of the picker-preview
    // slowdown sweep. Lineage:
    //   1800ms (original)
    //   → 3600ms (c41a04a doubling)
    //   → 7200ms (previous doubling)
    //   → 14400ms (this commit)
    // The eye couldn't track the count-up + fill-rise sweep at
    // 1800ms, and successive doublings still felt too quick
    // against the picker grid. 14.4s is long for a one-shot
    // wind-up; if it still feels wrong after this iteration,
    // the next move is converting to a looping wind-up (0 →
    // target → 0 → target → ...) rather than another doubling.
    // Kept in lockstep with the SVG arc wind-up keyframe
    // (evi-ring-fill below, also at 14.4s) so the number, fill,
    // and arc all finish at the same beat.
    const DURATION = 14400;
    fromValueRef.current = displayed;
    fromFillRef.current = fillY;
    startRef.current = null;

    function step(ts: number): void {
      if (startRef.current === null) startRef.current = ts;
      const t = Math.min(1, (ts - startRef.current) / DURATION);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayed(fromValueRef.current + (clamped - fromValueRef.current) * eased);
      setFillY(fromFillRef.current + (targetFillY - fromFillRef.current) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    }

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // `displayed` / `fillY` are read via refs so we ease from the current
    // visible value on every re-run; excluding them from deps is intentional.
  }, [clamped, targetFillY]);

  const vars = {
    ['--ring-end']: `${end.toFixed(2)}`,
    ['--ring-circumference']: `${C.toFixed(2)}`,
  } as CSSProperties;

  const clipPathIdDisc = `disc-${id}`;
  const clipPathIdFill = `fill-mask-${id}`;
  const gradientIdFill = `fill-${id}`;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: size,
        aspectRatio: '1 / 1',
        margin: '0 auto',
        ...vars,
      }}
      aria-label={`${Math.round(clamped)} percent ${label ?? 'state of charge'}`}
      role="img"
    >
      {/* Three-layer halo — each animates on its own cadence so the bloom
          never flatlines. All hot-path animations run on `transform` +
          `opacity` only; `filter: blur` is set once per layer.

          Historically these layers used `mix-blend-mode: screen`, but
          `screen(green, near-white) ≈ white` — so on the PWA light
          surface (#F7F5F0 / #FFFFFF) the halo effectively disappeared.
          Now using normal blend with solid-colour gradients that are
          visible on both light and dark backgrounds. */}

      {/* Far bloom — widest ambient spill, breathes slowly. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: '-40%',
          borderRadius: '50%',
          background: `radial-gradient(circle at center,
            color-mix(in srgb, ${ringColor} 75%, transparent) 0%,
            color-mix(in srgb, ${ringColor} 40%, transparent) 28%,
            color-mix(in srgb, ${ringColor} 12%, transparent) 55%,
            transparent 75%)`,
          filter: 'blur(28px)',
          pointerEvents: 'none',
          opacity: active ? 1 : 0.4,
          animation: active ? `evi-glow-far-${id} 3.4s ease-in-out infinite` : undefined,
          willChange: active ? 'transform, opacity' : undefined,
        }}
      />
      {/* Mid bloom — the primary pulsing halo. Saturated core so it
          actually reads on a white/cream surface. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: '-22%',
          borderRadius: '50%',
          background: `radial-gradient(circle at center,
            ${ringColor} 0%,
            color-mix(in srgb, ${ringColor} 70%, transparent) 25%,
            color-mix(in srgb, ${ringColor} 30%, transparent) 55%,
            transparent 78%)`,
          filter: 'blur(18px)',
          pointerEvents: 'none',
          opacity: active ? 1 : 0.55,
          animation: active ? `evi-glow-pulse-${id} 2.2s ease-in-out infinite` : undefined,
          willChange: active ? 'transform, opacity' : undefined,
        }}
      />
      {/* Near bloom — tight rim hotspot giving the ring a lit edge. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: '-6%',
          borderRadius: '50%',
          background: `radial-gradient(circle at center,
            color-mix(in srgb, ${ringColor} 55%, transparent) 0%,
            transparent 65%)`,
          filter: 'blur(8px)',
          opacity: active ? 1 : 0.6,
          pointerEvents: 'none',
        }}
      />

      <svg viewBox="0 0 220 220" width="100%" height="100%" style={{ position: 'relative' }}>
        <defs>
          <clipPath id={clipPathIdDisc}>
            <circle cx={CENTRE_X} cy={CENTRE_Y} r={INNER_R} />
          </clipPath>
          <clipPath id={clipPathIdFill}>
            {/* Mirror the liquid rect: text is visible wherever the liquid is. */}
            <rect
              x={CENTRE_X - INNER_R - 20}
              y={fillY}
              width={INNER_R * 2 + 40}
              height={BOTTOM_Y - fillY + 20}
            />
          </clipPath>
          <linearGradient id={gradientIdFill} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ringColor} stopOpacity="0.75" />
            <stop offset="40%" stopColor={ringColor} stopOpacity="0.9" />
            <stop offset="100%" stopColor={ringColor} stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Centre disc (punched out of the card surface so the liquid reads). */}
        <circle cx={CENTRE_X} cy={CENTRE_Y} r={INNER_R} fill="var(--evi-surface)" />

        {/* Energy liquid — clipped to the centre disc. */}
        <g clipPath={`url(#${clipPathIdDisc})`}>
          <rect
            x={CENTRE_X - INNER_R - 20}
            y={fillY}
            width={INNER_R * 2 + 40}
            height={BOTTOM_Y - fillY + 20}
            fill={`url(#${gradientIdFill})`}
          />
          {/* Two overlapping wave bands moving in opposite directions. */}
          <path
            d={wavePath(fillY, 0)}
            fill={ringColor}
            opacity="0.85"
            style={{
              animation: active ? `evi-wave-x-${id} 4s linear infinite` : undefined,
            }}
          />
          <path
            d={wavePath(fillY + 2, 20)}
            fill={ringColor}
            opacity="0.5"
            style={{
              animation: active ? `evi-wave-x-${id}-reverse 5.5s linear infinite` : undefined,
            }}
          />
        </g>

        {/* Track — full circumference in muted border. */}
        <circle
          cx={CENTRE_X}
          cy={CENTRE_Y}
          r={R}
          fill="none"
          stroke="var(--evi-border)"
          strokeWidth="10"
          opacity="0.5"
        />

        {/* Progress arc — fills on mount. */}
        <circle
          cx={CENTRE_X}
          cy={CENTRE_Y}
          r={R}
          fill="none"
          stroke={ringColor}
          strokeWidth="10"
          strokeLinecap="round"
          style={{
            transform: 'rotate(-90deg)',
            transformOrigin: `${CENTRE_X}px ${CENTRE_Y}px`,
            strokeDasharray: `0 ${C.toFixed(2)}`,
            // 4th iteration of the picker slowdown — 14.4s to
            // match the JS rAF wind-up above. Lineage 1.8s →
            // 3.6s → 7.2s → 14.4s. The two MUST stay in
            // lockstep — they paint the same "0 → target" arc,
            // just one as SVG stroke and one as fill-rect
            // height. A drift between them would read as the
            // number/fill landing before/after the arc
            // completes.
            animation: `evi-ring-fill-${id} 14.4s cubic-bezier(0.22, 1, 0.36, 1) 0.15s forwards`,
          }}
        />

        {/* Rim pulse — the arc breathes in place rather than sweeping. */}
        {active && (
          <circle
            cx={CENTRE_X}
            cy={CENTRE_Y}
            r={R}
            fill="none"
            stroke={ringColor}
            strokeWidth="10"
            strokeLinecap="round"
            style={{
              opacity: 0,
              transform: 'rotate(-90deg)',
              transformOrigin: `${CENTRE_X}px ${CENTRE_Y}px`,
              strokeDasharray: `${end.toFixed(2)} ${C.toFixed(2)}`,
              animation: `evi-rim-pulse-${id} 2.6s ease-in-out 2s infinite`,
              mixBlendMode: 'screen',
            }}
          />
        )}

        {/* Dual readout — neutral text with white twin clipped to the fill. */}
        <text
          x={CENTRE_X}
          y={CENTRE_Y + 4}
          textAnchor="middle"
          fill="var(--evi-text)"
          fontSize="40"
          fontWeight="400"
          letterSpacing="-1"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {displayed.toFixed(1)}
        </text>
        <g clipPath={`url(#${clipPathIdFill})`}>
          <text
            x={CENTRE_X}
            y={CENTRE_Y + 4}
            textAnchor="middle"
            fill="#FFFFFF"
            fontSize="40"
            fontWeight="400"
            letterSpacing="-1"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {displayed.toFixed(1)}
          </text>
        </g>
        <text
          x={CENTRE_X + 40}
          y={CENTRE_Y - 18}
          textAnchor="middle"
          fill={ringColor}
          fontSize="13"
          fontWeight="500"
        >
          %
        </text>
        {label && (
          <text
            x={CENTRE_X}
            y={CENTRE_Y + 26}
            textAnchor="middle"
            fill="var(--evi-text-mute)"
            fontSize="9"
            letterSpacing="2.5"
          >
            {label.toUpperCase()}
          </text>
        )}
      </svg>

      <style>{`
        @keyframes evi-ring-fill-${id} {
          from { stroke-dasharray: 0 ${C.toFixed(2)}; }
          to   { stroke-dasharray: ${end.toFixed(2)} ${C.toFixed(2)}; }
        }
        @keyframes evi-rim-pulse-${id} {
          0%, 100% { opacity: 0; }
          50%      { opacity: 0.55; }
        }
        @keyframes evi-glow-pulse-${id} {
          0%, 100% { transform: scale(0.95); opacity: 0.75; }
          50%      { transform: scale(1.14); opacity: 1;    }
        }
        /* Far bloom breathes offset from the mid halo — keeps the
           overall bloom alive even at the mid's trough. */
        @keyframes evi-glow-far-${id} {
          0%, 100% { transform: scale(1);    opacity: 0.55; }
          50%      { transform: scale(1.08); opacity: 1;    }
        }
        /* Wave bands translate 100px so the pattern loops seamlessly against
           the wavePath() function's 100-unit horizontal period. */
        @keyframes evi-wave-x-${id} {
          from { transform: translateX(0); }
          to   { transform: translateX(-100px); }
        }
        @keyframes evi-wave-x-${id}-reverse {
          from { transform: translateX(-100px); }
          to   { transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [aria-label*="percent ${label ?? 'state of charge'}"] [style*="evi-glow-far"],
          [aria-label*="percent ${label ?? 'state of charge'}"] [style*="evi-glow-pulse"],
          [aria-label*="percent ${label ?? 'state of charge'}"] [style*="evi-rim-pulse"],
          [aria-label*="percent ${label ?? 'state of charge'}"] [style*="evi-wave-x"] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Builds a horizontal wave path that tiles every 100 units — combined
 * with a −100px translate keyframe it loops seamlessly. `yOffset` lets
 * the second band sit slightly below the first.
 */
function wavePath(surfaceY: number, yOffset: number): string {
  const y = surfaceY + yOffset;
  const amp = 5;
  // Four quarter-periods at 25 units each → total 100-unit period.
  const segments: string[] = [`M -20 ${y.toFixed(2)}`];
  for (let x = 0; x <= 260; x += 25) {
    const cy = (x / 25) % 2 === 0 ? y - amp : y + amp;
    segments.push(`Q ${(x + 12.5).toFixed(2)} ${cy.toFixed(2)} ${(x + 25).toFixed(2)} ${y.toFixed(2)}`);
  }
  segments.push(`L 280 ${(y + 40).toFixed(2)}`);
  segments.push(`L -20 ${(y + 40).toFixed(2)}`);
  segments.push('Z');
  return segments.join(' ');
}
